<template>
  <div>
    <page-header
      :actions="headers.actions"
      left-side-class="col-xl-4 pb-2"
      :page="headers.page"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <div
      v-if="isReturned"
      class="p-3 returned-box"
      :style="{ borderLeftColor: model.status?.color }"
    >
      <icon-view
        icon="fas fa-undo mb-1"
        icon-size="16px"
        :style="{ '--icon-color': model.status?.color }"
        title="Devuelto"
      />
      <div class="m-2">
        <div class="font-16 color-black">
          Razón de devolución:
        </div>
        <div class="font-14 color-gray-6">
          {{ model.logs | first(model.status?.id) | empty }}
        </div>
      </div>
    </div>
    <board class="my-3" :details="board">
      <template #data-item="{ item }">
        <div v-if="item.status">
          <custom-balloon
            :background-color="item.status.color"
            dot
            :text="item.status.name"
          />
        </div>
      </template>
    </board>
    <validation-observer ref="formValidator">
      <b-tabs
        v-model="tab"
        :class="{ 'three-tabs': isSubmitted }"
        content-class="my-3"
        nav-class="custom-nav-container"
        no-nav-style
      >
        <b-tab
          id="request"
          class="purchase-service-tabs"
          :lazy="false"
          title="Solicitud Contratación de Servicios"
        >
          <template #title>
            <span :class="{ 'tab-title-error': tabErrorKey === 'request' }">
              Solicitud Contratación de Servicios
              <i
                v-if="tabErrorKey === 'request'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <div class="my-3">
            <div class="row my-2">
              <div class="col-12 d-flex justify-content-lg-end">
                <action-button
                  class="outline-dark-gray"
                  :i="
                    anyCollapseOpen
                      ? 'fal fa-compress-alt'
                      : 'fal fa-expand-alt'
                  "
                  :text="anyCollapseOpen ? 'Colapsar Todo' : 'Expandir Todo'"
                  @click="expandAll(!anyCollapseOpen)"
                />
              </div>
            </div>

            <request-information
              :id="id"
              v-model="model"
              :government-levels="catalogs.governmentLevels"
              :is-amendment-renewal="isAmendmentRenewal"
              :is-read-only="isSubmitted"
              :programs="catalogs.programs"
              @contractors="onCompetitionProcessContractsChange"
              @reset-contractors="resetContracts"
              @search="onSearchPrevious"
            />
            <contact-info
              v-model="model.detail"
              :is-read-only="isSubmitted"
              :offices="catalogs.contactOffices"
              :show-slot="!isAmendmentRenewal"
            />
            <validation-provider
              v-if="canView(sections.ContractorContactInformation)"
              v-slot="{ validated }"
              rules="required"
            >
              <contractor-info
                ref="contractorInfo"
                v-model="model.detail.contractors"
                :contractors="contractors"
                :is-competition-process-from-mipe="
                  model.detail?.isCompetitionProcessFromMipe
                "
                :is-read-only="isSubmitted"
                @load-contractors="onLoadContractors"
              >
                <template #contractor-error>
                  <error
                    v-if="validated && !model.detail.contractors?.length"
                    message="Debe haber al menos 1 contratista agregado."
                    variant="no-border"
                  />
                </template>
              </contractor-info>
            </validation-provider>

            <contract-justification
              v-if="canView(sections.ContractJustification)"
              v-model="model.detail.contractJustification"
              :is-read-only="isSubmitted"
            />
            <public-funds-services
              v-if="canView(sections.PublicFundsServicesSection)"
              v-model="model.detail.serviceRelationshipPublicFunds"
              :is-read-only="isSubmitted"
            />
            <explanatory-fields
              v-if="canView(sections.ExplanatoryFields)"
              v-model="model.detail"
              :government-levels="catalogs.governmentLevels"
              :is-read-only="isSubmitted"
            />
            <collapse
              v-if="canView(sections.BudgetSourceFunds)"
              title="Partida presupuestaria y/o procedencia de los fondos"
            >
              <budget-source-funds
                :is-read-only="isSubmitted"
                :model="model.detail"
                :program-types="catalogs.governmentLevels"
              />
            </collapse>

            <services-requested
              v-if="canView(sections.ServicesRequested)"
              :is-read-only="isSubmitted"
              :model="model.detail"
            />
            <contract-information
              v-if="canView(sections.ContractInformation)"
              ref="contractInformation"
              v-model="model.detail"
              :contractors="model.detail.contractors"
              :contracts="contracts"
              :determination-categories="catalogs.determinationCategories"
              :determination-category="previousRequest?.determinationCategoryId"
              :is-read-only="isSubmitted"
              :program-types="catalogs.governmentLevels"
              :type="model.contractType?.id"
              @load-contracts="onLoadContracts"
            />
            <payment-amount
              v-if="canView(sections.PaymentAmount)"
              v-model="model.detail.competitionProcessContracts"
              :is-amount-required="hasBudgetSourceFunds"
              :is-read-only="isSubmitted"
            />
            <cost-analysis
              v-if="canView(sections.CostAnalysis)"
              v-model="model.detail"
              :is-read-only="isSubmitted"
            />
            <contract-renewal
              v-if="canView(sections.ContractRenewal)"
              :is-read-only="isSubmitted"
              :model="model.detail"
            />
            <full-description
              v-if="canView(sections.FullDescription)"
              ref="fullDescriptionFields"
              v-model="model.detail"
              :is-read-only="isSubmitted"
            />
          </div>
        </b-tab>
        <b-tab id="documents" class="purchase-service-tabs" :lazy="false">
          <template #title>
            <span :class="{ 'tab-title-error': tabErrorKey === 'documents' }">
              Documentos de Respaldo
              <i
                v-if="tabErrorKey === 'documents'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <supporting-documents
            ref="supportingDocuments"
            v-model="model.supportingDocuments"
            :document-types="catalogs.supportingDocumentTypes"
            :is-read-only="isSubmitted"
          />
        </b-tab>
        <b-tab
          v-if="canViewSignedTab"
          id="signature"
          class="purchase-service-tabs"
          lazy
        >
          <template #title>
            <span :class="{ 'tab-title-error': tabErrorKey === 'signature' }">
              Firma
              <i
                v-if="tabErrorKey === 'signature'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <div v-if="loadingSignature" class="mt-3">
            <b-card>
              <b-row>
                <b-col>
                  <b-skeleton width="85%" />
                  <b-skeleton width="55%" />
                </b-col>
                <b-col>
                  <b-skeleton width="70%" />
                  <b-skeleton width="55%" />
                </b-col>
                <b-col>
                  <b-skeleton width="85%" />
                  <b-skeleton width="55%" />
                </b-col>
                <b-col>
                  <b-skeleton width="70%" />
                  <b-skeleton width="55%" />
                </b-col>
                <b-col>
                  <b-skeleton type="button" width="100%" />
                </b-col>
              </b-row>
            </b-card>
          </div>
          <template v-if="!loadingSignature">
            <sign-plan
              ref="signPlanRef"
              class="my-4"
              :disabled="isElectronicDisabled"
              :is-electronic-signature.sync="model.isElectronicSignature"
              question-label="¿Cuál será el mecanismo que se estará utilizando para firmar la Solicitud Contratación de Servicios?"
            />
            <div class="mt-4">
              <div v-if="hasSelectMechanisManuscript">
                <section-title has-line title="Documento" />
                <div
                  v-if="document"
                  class="row bg-secondary-3 rounded p-3 mb-3 ml-1 mr-0"
                >
                  <div class="col-md-6">
                    <div class="color-gray-6 font-14 font-weight-normal">
                      Tipo de Documento:
                    </div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ document.documentTypeName }}
                    </div>
                  </div>
                  <div class="col-md-6 d-flex justify-content-center">
                    <div v-if="hasManuscriptDocUploaded && !editingDocument">
                      <action-button
                        v-if="canChangeSignedDocument"
                        class="mr-2 mt-1 outline-success"
                        i="fas fa-undo mr-n1 pr-1"
                        variant="success"
                        @click="() => (editingDocument = true)"
                      />
                      <action-button
                        class=" mt-1"
                        i="fas fa-file-download text-white mr-n1 pr-1"
                        variant="success"
                        @click="downloadDocument"
                      />
                    </div>
                    <div v-else-if="hasDownloadedTemplate || editingDocument">
                      <span
                        class="color-gray-6 font-14 font-weight-normal mr-3 mt-2"
                      >
                        Documento:
                      </span>
                      <validation-provider
                        v-slot="{ errors }"
                        :rules="{ required: true }"
                      >
                        <custom-file-input
                          ref="documentInform"
                          v-model="document.selectedDocument"
                          :accept="'.pdf'"
                          :attachment-id="document.documentId"
                          custom-error-msg="Favor de anejar un documento."
                          display-style="normal"
                          :error="errors[0]"
                          label-position="left"
                          required
                          @input="saveSignedDocument($event)"
                        />
                      </validation-provider>
                    </div>
                    <action-button
                      v-else
                      class="mt-1"
                      i="fas fa-file-download text-white mr-n1 pr-1"
                      variant="success"
                      @click="downloadTemplate"
                    />
                  </div>
                </div>
              </div>
              <signature-hub-info
                v-else-if="hasSelectMechanismElectronic"
                :signature-info="signatureHubInfo"
                @configure-sign="handleConfigureSignatures"
              />
            </div>
          </template>
        </b-tab>
      </b-tabs>
    </validation-observer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from '@/mixins/alert.mixin.js';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import Board from 'Board';
import SectionTitle from 'SectionTitle';
import CustomBalloon from 'custom-balloon';
import Error from 'custom-error';
import { CustomFileInput } from '@/components/common';
import IconView from 'icon-view';

import ContractorInfo from './sections/ContractorInfo.vue';
import ContactInfo from './sections/ContactInfo.vue';
import RequestInformation from './sections/RequestInformation.vue';
import ContractRenewal from './sections/ContractRenewal.vue';
import FullDescription from './sections/FullDescription.vue';
import ExplanatoryFields from './sections/ExplanatoryFields.vue';
import SupportingDocuments from './sections/SupportingDocuments.vue';
import ContractInformation from './sections/ContractInformation.vue';
import PaymentAmount from './sections/PaymentAmount.vue';
import CostAnalysis from './sections/CostAnalysis.vue';
import BudgetSourceFunds from './sections/BudgetSourceFunds.vue';
import ServicesRequested from './sections/ServicesRequested.vue';
import ContractJustification from './sections/ContractJustification.vue';
import PublicFundsServices from './sections/PublicFundsServices.vue';
import Collapse from '@/components/common/SimpleCollapse.vue';

import _generalApi from '@/modules/app/api';
import { serialize as objectToFormData } from 'object-to-formdata';
import {
  getById,
  getCatalogs,
  save,
  saveMechanism,
  saveSignedDocument,
  withContractAnnouncement,
  getCompanyContracts,
} from '@/modules/purchaseService/api.js';

import { SignPlan } from '@/views/provider/referral-management/search/components/sign-plan';
import { SignatureHubInfo } from '@quisit/signature-hub';
import {
  getSignatureHubInfo as _getSignatureHubInfo,
  removeSignatureHub as _removeSignatureHub,
} from '@/modules/signatureHub/api';
import DocumentTypes from '@/utils/constants/documentTypes';

import SignatureHubTypes from '@/utils/constants/signatureHubTypes.js';
import PurchaseServiceStatus from '@/utils/constants/PurchaseServiceStatus';
import Roles from '@/utils/constants/roles.js';
import ContractType from '@/utils/constants/purchaseServiceContractType';
import DeterminationCategory from '@/utils/constants/purchaseServiceDeterminationCategories';
import { changeStatus } from '@/modules/purchaseService/api.js';

const Tabs = Object.freeze({
  ServicePurchaseRequest: 0,
  SupportingDocuments: 1,
  Signature: 2,
});

const Sections = Object.freeze({
  RequestInformation: 0,
  ContractorContactInformation: 2,
  ContractJustification: 3,
  PublicFundsServicesSection: 4,
  ExplanatoryFields: 5,
  ContractInformation: 8,
  BudgetSourceFunds: 10,
  ServicesRequested: 11,
  ContractRenewal: 12,
  FullDescription: 13,
  PaymentAmount: 14,
  CostAnalysis: 15,
});

const DigitalSignatureStatuses = Object.freeze({ Pendiente: 3, Firmado: 5 });

export default {
  name: 'PurchaseServiceDetail',
  components: {
    Board,
    ValidationObserver,
    ValidationProvider,
    SignatureHubInfo,
    SignPlan,
    SectionTitle,
    CustomFileInput,
    CustomBalloon,
    Error,
    IconView,
    ContactInfo,
    ExplanatoryFields,
    SupportingDocuments,
    RequestInformation,
    ContractRenewal,
    FullDescription,
    ContractorInfo,
    BudgetSourceFunds,
    ServicesRequested,
    ContractJustification,
    PublicFundsServices,
    ContractInformation,
    PaymentAmount,
    CostAnalysis,
    Collapse,
  },
  filters: {
    first(logs, statusId) {
      if (!logs?.length) return '';
      const log = logs.find((l) => l.newStatusId === statusId);
      return log ? log.comment : '';
    },
  },
  mixins: [alertMixin],
  data() {
    return {
      tabErrorKey: null,
      loadingSignature: false,
      editingDocument: false,
      catalogs: {
        contactOffices: [],
        determinationCategories: [],
      },
      model: {
        detail: {
          isContractRenewal: null,
          contractRenewalStartDate: null,
          contractRenewalEndDate: null,
        },
        isElectronicSignature: null,
      },
      signatureHubInfo: null,
      document: {
        documentTypeName: 'Reporte de PSF',
        documentTypeId: 1630,
        selectedDocument: null,
        documentId: null,
      },
      hasDownloadedTemplate: false,
      collapseStates: {},
      collapseSetters: {},
      contractors: null,
      contracts: null,
      previousRequest: null,
    };
  },
  provide() {
    return {
      registerCollapse: this.registerCollapse,
      notifyCollapse: this.notifyCollapse,
    };
  },
  computed: {
    ...mapGetters('$_user', ['fullProfile']),
    anyCollapseOpen() {
      const values = Object.values(this.collapseStates);
      return values.some(Boolean);
    },
    id() {
      return Number(this.$route.params.id);
    },
    isElectronicDisabled() {
      return [
        this.isElectronicSigned,
        this.isElectronicPending,
        this.hasManuscriptDocUploaded,
      ].includes(true);
    },
    isElectronicPending() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatusId ===
        DigitalSignatureStatuses.Pendiente
      );
    },
    isElectronicSigned() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatusId ===
        DigitalSignatureStatuses.Firmado
      );
    },
    hasSelectMechanisManuscript() {
      return this.model.isElectronicSignature === false;
    },
    hasSelectMechanismElectronic() {
      return this.model.isElectronicSignature === true;
    },
    hasManuscriptDocUploaded() {
      return !!this.model.signedDocumentId;
    },
    isSubmitted() {
      return Boolean(this.model.submitted);
    },
    canViewSignedTab() {
      return [
        PurchaseServiceStatus.Approved,
        PurchaseServiceStatus.Signed,
        PurchaseServiceStatus.Determined,
        PurchaseServiceStatus.Awarded,
      ].includes(this.model.status?.id);
    },
    canViewSignDocument() {
      return (
        this.model.status?.id === PurchaseServiceStatus.Approved &&
        this.hasManuscriptDocUploaded
      );
    },
    isReturned() {
      return this.model.status?.id === PurchaseServiceStatus.Returned;
    },
    canSubmit() {
      return !this.isSubmitted && (this.createdByMe || this.isAdmin);
    },
    isAdmin() {
      return [
        Roles.Administrador,
        Roles.AdminProvider,
        Roles.AdminSAEE,
        Roles.AdminIL,
        Roles.UMESRAdmin,
      ].includes(this.fullProfile?.role);
    },
    createdByMe() {
      return this.model.createdById === this.fullProfile?.sub;
    },
    tab: {
      get() {
        return Number(this.$route.query.tab ?? Tabs.ServicePurchaseRequest);
      },
      set(tab) {
        const same = this.tab === tab;
        if (same) return;
        const payload = tab ? { query: { tab } } : { name: this.$route.name };
        this.$router.replace(payload);
      },
    },
    headers() {
      return {
        page: {
          title: 'Solicitud Contratación de Servicios',
          icon: 'fas fa-building',
          routes: [
            'Proceso de Competencias',
            'Solicitud Contratación de Servicios',
          ],
        },
        actions: [
          {
            text: 'Regresar',
            icon: 'fas fa-arrow-circle-left',
            variant: 'outline-success',
            click: () => this.$router.push({ name: 'purchaseService' }),
          },
          {
            text: 'Guardar',
            icon: 'fas fa-save',
            variant: 'outline-success',
            visible: !this.isSubmitted,
            click: () => this.onSaveHandler(),
          },
          {
            text: this.isReturned ? 'Reenviar' : 'Someter',
            icon: this.isReturned ? 'fas fa-paper-plane' : 'fas fa-flag',
            variant: 'success',
            visible: this.canSubmit,
            click: () => this.onSaveHandler(true),
          },
          {
            text: 'Firmar',
            icon: 'fas fa-pen-alt',
            variant: 'success',
            visible: this.canViewSignDocument,
            click: () => this.markSigned(),
          },
        ],
      };
    },
    board() {
      return [
        [
          {
            label: 'Estatus:',
            icon: 'fas fa-file-alt',
            status: this.model.status,
          },
          {
            label: 'Número de Solicitud:',
            icon: 'fa fa-file-invoice',
            data: this.model.number,
          },
          {
            label: 'Área:',
            icon: 'fas fa-folder',
            data: this.model.area?.name,
          },
          {
            label: 'Servicio a Ofrecer:',
            icon: 'fas fa-clipboard',
            data: this.model.service?.name,
          },
          {
            label: 'Fecha de Solicitud:',
            icon: 'fas fa-calendar-edit',
            data: this.$formatDate(this.model.date),
          },
          {
            label: 'Tipo de Contratación:',
            icon: 'fas fa-file-alt',
            data: this.model.contractType?.name,
          },
        ],
      ];
    },
    isAmendmentRenewal() {
      return [ContractType.Renewal, ContractType.Amendment].includes(
        this.model.contractType?.id
      );
    },
    isRenewal() {
      return this.model.contractType?.id === ContractType.Renewal;
    },
    isNewContract() {
      return this.model.contractType?.id === ContractType.New;
    },
    hasContractOriginalCopy() {
      return (this.model.supportingDocuments || []).some(
        (d) =>
          d?.documentType?.id ===
          DocumentTypes.OriginalCopyPurchaseServiceSupportingDocument
      );
    },
    hasCostEstimate() {
      return (this.model.supportingDocuments || []).some(
        (d) => d?.documentType?.id === DocumentTypes.CostEstimate
      );
    },
    hasBudgetSourceFunds() {
      return Boolean(
        this.model.detail?.purchaseOrderNumber?.some((x) => !x.isActual)
      );
    },
    sections() {
      return Sections;
    },
    canView() {
      return (id) => {
        switch (this.model.contractType?.id) {
          case ContractType.New:
            return [
              Sections.ContractRenewal,
              Sections.FullDescription,
              Sections.ContractJustification,
              Sections.PublicFundsServicesSection,
              Sections.BudgetSourceFunds,
              Sections.ServicesRequested,
              Sections.ExplanatoryFields,
            ].includes(id);
          case ContractType.Renewal:
            return [
              Sections.RequestInformation,
              Sections.ServicesRequested,
              Sections.ContractInformation,
              Sections.PaymentAmount,
              Sections.CostAnalysis,
              Sections.ContractorContactInformation,
              Sections.ContractJustification,
              Sections.PublicFundsServicesSection,
            ].includes(id);
          case ContractType.Amendment:
            return [
              Sections.RequestInformation,
              Sections.ServicesRequested,
              Sections.ContractInformation,
              Sections.PaymentAmount,
              Sections.CostAnalysis,
              Sections.ContractorContactInformation,
              Sections.ContractJustification,
              Sections.PublicFundsServicesSection,
            ].includes(id);

          default:
            return false;
        }
      };
    },
    canChangeSignedDocument() {
      return this.userHasPermissions(
        'provider.psf.service.requests.changesigneddocument'
      );
    },
  },
  watch: {
    'model.isElectronicSignature': {
      handler(val, old) {
        this.handleSelectMechanism(val, old);
      },
    },
  },
  async mounted() {
    await this.getInfo();
    await this.getCatalogs();
  },
  methods: {
    registerCollapse(id, getOpen, setOpen) {
      // estado inicial
      this.$set(this.collapseStates, id, !!getOpen());
      this.$set(this.collapseSetters, id, setOpen);

      // devolver función para desregistrar
      return () => {
        this.$delete(this.collapseStates, id);
        this.$delete(this.collapseSetters, id);
      };
    },
    notifyCollapse(id, open) {
      if (!id) return;
      this.$set(this.collapseStates, id, !!open);
    },
    expandAll(open = true) {
      Object.values(this.collapseSetters).forEach((fn) => fn?.(open));
    },
    async getInfo() {
      const { data } = await getById(this.id);
      this.model = data;
      await this.onLoadContracts();
      if (this.model.detail.announcementNumber) {
        await this.onLoadContractors();
      }
      if (this.hasSelectMechanismElectronic) await this.getSignatureHubInfo();
    },
    async getCatalogs() {
      const payload = { areaid: this.model.area?.id };
      const { data } = await getCatalogs(payload);
      this.catalogs = data;

      if (this.catalogs.contactOffices.length === 1 && !this.model.submitted) {
        this.model.detail.contactOffice =
          this.catalogs.contactOffices[0] || null;
      }
    },
    async onSaveHandler(submitted = false) {
      this.tabErrorKey = null;
      await this.$nextTick();

      if (this.model.number === this.model.detail.parentNumber) {
        this.ShowToast(
          '',
          'El número de PSF no puede ser el mismo que el número de la solicitud.',
          'error'
        );
        this.changeTabError('request');
        return;
      }

      if (submitted && this.canSubmit) {
        if (this.model.detail.isFromMipe && !this.model.detail.parentNumber) {
          this.ShowToast(
            '',
            'Debe seleccionar un número de solicitud de compra del servicio.',
            'error'
          );

          this.changeTabError('request');
          return;
        }

        if (
          this.model.detail.isCompetitionProcessFromMipe &&
          !this.model.detail.announcementNumber
        ) {
          this.ShowToast('', 'Debe seleccionar un número de pliego.', 'error');
          this.changeTabError('request');
          return;
        }

        if (
          [ContractType.New].includes(this.model.contractType?.id) &&
          !this.model.detail.purchaseOrderNumber.length > 0
        ) {
          this.ShowToast(
            '',
            'Debe tener al menos una partida presupuestaria y/o procedencia de los fondos.',
            'error'
          );
          this.changeTabError('request');
          return;
        }

        const hasDocuments = this.model.supportingDocuments.length > 0;
        if (!hasDocuments) {
          this.ShowToast(
            '',
            'Debe tener al menos un documento para someter.',
            'error'
          );
          this.changeTabError('documents');
          return;
        }
        if (this.isRenewal && !this.hasContractOriginalCopy) {
          this.ShowToast(
            '',
            'Debe tener al menos un documento de tipo Copia Contrato Original para someter.',
            'error'
          );
          this.changeTabError('documents');
          return;
        }
        if (this.isNewContract && !this.hasCostEstimate) {
          this.ShowToast(
            '',
            'Debe tener al menos un documento de tipo Estimado de Costo para someter.',
            'error'
          );
          this.changeTabError('documents');
          return;
        }
        await this.$nextTick();
        const formOk = await this.$refs.formValidator.validate();
        const kidsOk =
          (await this.$refs.fullDescriptionFields?.validateChildrenOnSubmit?.()) ??
          true;
        if (!formOk || !kidsOk) {
          this.ShowToast(
            '',
            'Debe completar todos los campos obligatorios antes de continuar.',
            'error'
          );

          await this.showTabWithError();
          return;
        }

        const message = this.isReturned
          ? '¿Está seguro que desea reenviar esta solicitud? Se enviará nuevamente a revisión y no podrá editarla hasta recibir respuesta?'
          : '¿Está seguro que desea someter la Solicitud Contratación de Servicios?';
        const answer = await this.ShowCancelModal(
          'Confirmación',
          message,
          'Si',
          'No'
        );

        if (!answer) return;
      }

      try {
        const payload = { ...this.getCleanModel(), submitted };
        await save(payload);
        this.ShowToast('', 'Información guardada exitosamente.', 'success');
        if (this.tab === Tabs.ServicePurchaseRequest) {
          this.$refs.formValidator.reset();
        }
        await this.getInfo();
      } catch {
        const action = this.isReturned
          ? 'reenviar'
          : submitted
          ? 'someter'
          : 'guardar';
        this.ShowToast(
          '',
          `No fue posible ${action} la solicitud. Intente nuevamente o contacte al administrador.`,
          'error'
        );
      }
    },
    async getSignatureHubInfo() {
      try {
        this.loadingSignature = true;
        const { data } = await _getSignatureHubInfo(
          SignatureHubTypes.PURCHASE_REQUEST,
          Number(this.id)
        );
        this.signatureHubInfo = data;
        this.loadingSignature = false;
      } catch (error) {
        this.loadingSignature = false;
        this.ShowToast(
          'Error',
          'No se pudo obtener la información de firma.',
          'error'
        );
      }
    },
    async handleSelectMechanism(value, previousValue) {
      const toManuscript = value === false && previousValue === true;
      const toElectronic = value === true && previousValue === false;

      if (typeof value === 'boolean') {
        await saveMechanism(this.id, value).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
      }

      if (toElectronic && this.document?.selectedDocument)
        await this.setToElectronic();
      else if (toManuscript && this.signatureHubInfo?.signatureHubId) {
        const result = await this.setToManuscript();
        if (result) await this.getSignatureHubInfo();
      }
    },
    async saveSignedDocument(data) {
      const payload = objectToFormData(
        {
          id: this.id,
          file: data,
        },
        {
          indices: true,
          nullsAsUndefineds: true,
        }
      );

      try {
        await saveSignedDocument(payload);

        this.ShowToast('', 'Documento guardado exitosamente.', 'success');
        await this.getInfo();

        if (this.editingDocument) {
          this.editingDocument = false;
          return;
        }

        await this.markSigned();
      } catch {
        this.ShowToast(
          '',
          'Ocurrió un error al intentar guardar la información.',
          'error'
        );
      }
    },
    async handleConfigureSignatures() {
      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: SignatureHubTypes.PURCHASE_REQUEST,
          relatedId: this.id,
          from: 'purchaseServiceDetail',
          fromData: {
            id: this.id,
          },
        },
        query: { tab: Tabs.Signature },
      });
    },
    async setToElectronic() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        'Usted ya se encuentra en un proceso de configuración de firma Manuscrito. ¿Está seguro que desea cambiar la configuración a Electronica?',
        'Sí',
        'No'
      );
      if (!confirmed) {
        this.model.isElectronicSignature = false;
        this.$nextTick(() => this.$refs.documentInform.loadFile());
        return false;
      }
      this.document.selectedDocument = null;
      return true;
    },
    async setToManuscript() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        'Usted ya se encuentra en un proceso de configuración de firma digital. ¿Está seguro que desea cambiar la configuración a manuscrito?',
        'Sí',
        'No'
      );

      if (!confirmed) {
        this.model.isElectronicSignature = true;
        return false;
      }
      try {
        await _removeSignatureHub(
          SignatureHubTypes.PURCHASE_REQUEST, //to-do
          this.id
        );
        this.signatureHubInfo = null;
        return true;
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
        return false;
      }
    },
    async downloadTemplate() {
      try {
        await _generalApi.getPurchaseService(this.id);
        this.hasDownloadedTemplate = true;
      } catch (err) {
        this.ShowToast('', err.response.data.message || err.message, 'error');
      }
    },
    async downloadDocument() {
      await _generalApi.downloadDocument(this.model.signedDocumentId);
    },
    async onCompetitionProcessContractsChange({
      announcementId,
      isCompetitionProcessFromMipe,
    }) {
      this.resetContracts();

      if (isCompetitionProcessFromMipe && announcementId) {
        const { data: contractors } = await withContractAnnouncement({
          announcementId: announcementId,
        });

        const { data: contracts } = await getCompanyContracts({
          announcementId: announcementId,
        });

        this.contractors = contractors;
        this.contracts = contracts;
      }
    },
    async onLoadContractors(queryParams = {}) {
      this.resetContracts();

      const { data: contractors } = await withContractAnnouncement({
        announcementNumber: this.model.detail?.announcementNumber,
        ssn: queryParams.ssn ?? '',
        name: queryParams.name ?? '',
        currentPage: queryParams.currentPage ?? 1,
        pageSize: queryParams.pageSize ?? 5,
      });

      this.contractors = contractors;
    },
    async onLoadContracts(queryParams = {}) {
      this.resetContracts();

      if (
        this.model.detail?.isCompetitionProcessFromMipe &&
        this.model.detail?.announcementNumber
      ) {
        const { data: contracts } = await getCompanyContracts({
          announcementNumber: this.model.detail?.announcementNumber,
          name: queryParams.name ?? '',
          currentPage: queryParams.currentPage ?? 1,
          pageSize: queryParams.pageSize ?? 5,
        });

        this.contracts = contracts;
      }
    },
    resetContracts(isCompetitionProcessFromMipe = true) {
      this.contractors = {
        data: [],
        totalItemCount: 0,
        currentPage: 1,
        count: 0,
        pageCount: 5,
      };

      this.contracts = {
        data: [],
        totalItemCount: 0,
        currentPage: 1,
        count: 0,
        pageCount: 5,
      };

      if (!isCompetitionProcessFromMipe) {
        this.model.detail.contractors = [];
        this.model.detail.competitionProcessContracts = [];
      }
    },
    getCleanModel() {
      const model = { ...this.model };
      if (
        model.detail.contractDetailOriginalVia !==
        DeterminationCategory.Exception
      ) {
        model.detail.contractDetailOriginalViaException = null;
      }
      if (
        model.detail.contractDetailOriginalVia !==
        DeterminationCategory.Quotation
      ) {
        model.detail.contractDetailOriginalViaQuotation = null;
      }
      if (
        model.detail.contractDetailOriginalVia !== DeterminationCategory.RFP
      ) {
        model.detail.contractDetailOriginalViaRenovationYearsQuantity = null;
        model.detail.contractDetailOriginalViaSignatureYear = null;
      }
      if (
        this.model.contractType?.id === ContractType.Amendment &&
        !model.detail.contractDetailValidityChanged
      ) {
        model.detail.contractDetailValidityStartDate = null;
        model.detail.contractDetailValidityEndDate = null;
      }
      return model;
    },
    onSearchPrevious(data) {
      this.previousRequest = data;
    },
    async markSigned() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea cambiar a firmado la solicitud?',
        'Sí',
        'No'
      );

      if (confirmed) {
        const data = {
          statusId: PurchaseServiceStatus.Signed,
        };

        const payload = objectToFormData(data, {
          indices: true,
          nullsAsUndefineds: true,
        });

        await changeStatus(this.id, payload);

        await this.getInfo();
      }
    },
    changeTabError(key) {
      const element = this.$el.querySelector(`#${key}`);

      const panes = Array.from(
        this.$el.querySelectorAll('.purchase-service-tabs')
      );
      const tabIndex = panes.indexOf(element);

      if (tabIndex > -1) {
        this.tab = tabIndex;
      }

      this.tabErrorKey = key;
    },
    async showTabWithError() {
      // Intentar identificar la primera entrada inválida y cambiar a la pestaña correspondiente
      await this.$nextTick();
      const invalidEl = this.$el.querySelector(
        '.was-Invalidated, .is-invalid, .invalid, [aria-invalid="true"]'
      );
      if (!invalidEl) return;

      // Buscar el panel de tab que contiene el elemento inválido.
      const pane = invalidEl.closest('[role="tabpanel"], .tab-pane');
      if (!pane) return;

      // marcar la pestaña con error por su id (si existe)
      if (pane.id) {
        this.tabErrorKey = pane.id;
      }

      const panes = Array.from(
        this.$el.querySelectorAll('.purchase-service-tabs')
      );

      const tabIndex = panes.indexOf(pane);
      if (tabIndex > -1) {
        this.tab = tabIndex;
      }
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';

::v-deep .orange-icon {
  font-size: 18pt;
}
::v-deep .custom-tab ul.custom-tab-container {
  -webkit-box-pack: center !important;
  -ms-flex-pack: center !important;
  justify-content: center !important;
  margin: 0 !important;
}

::v-deep .custom-tab ul.custom-tab-container > li {
  text-align: center !important;
  width: 33.3% !important;
}

::v-deep .custom-tab.three-tabs ul.custom-tab-container > li {
  text-align: center !important;
  width: 33.33% !important;
}

.custom-date-picker ::v-deep .width-container {
  width: 100% !important;
}
.text-brown {
  color: #d5b89a !important;
}
.returned-box {
  border-left: 5px solid;
  background-color: #fafafa !important;
  border-radius: 5px;
}

.returned-box ::v-deep .dot {
  color: var(--icon-color);
  margin-right: 8px !important;
}

::v-deep .custom-nav-container {
  border-bottom: 1px solid map-get($colors, 'gray-5');
  .nav-item {
    .nav-link {
      font-size: 17.602px;
      font-weight: 700;
      color: map-get($colors, 'secondary-6');
      &.active {
        border-bottom: 3px solid map-get($colors, 'green');
        color: map-get($colors, 'green');
      }
      &.active:after {
        content: unset;
      }
    }
  }
}

i.fas.fa-exclamation-triangle {
  color: #ff5b5c !important;
}

::v-deep .custom-nav-container {
  .nav-item {
    .nav-link {
      .tab-title-error {
        color: #ff5b5c !important;
        display: inline-block;
        padding-bottom: 4px;
      }
    }
  }
}

::v-deep .custom-nav-container {
  .nav-item {
    .nav-link.active:has(.tab-title-error) {
      border-bottom: 3px solid #ff5b5c;
    }
  }
}
</style>
